import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEventsError, trackEventsEvent } from "../analytics/events";
import { useCreateEventMutation } from "./useCreateEventMutation";
import { useCreateEventTemplateMutation } from "./useEventTemplateMutations";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import { useCachedTerreiro } from "./useCachedTerreiro";
import type { CreateEventPayload } from "../repositories/eventsRepository";
import type { FormChatState } from "../repositories/formChat.repository";
import { buildEventFormChatPayload } from "../utils/eventFormChatPayloadBuilder";
import { buildPathWithStatusTabSearch } from "../utils/statusTabNavigation";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import { toast } from "../utils/toast";

interface SubmitEventByChatInput {
  data: FormChatState;
  memberFromActivityMap?: Record<string, boolean>;
  insumoFromActivityMap?: Record<string, boolean>;
  modelId?: string;
}

export function useFormChatSubmitEvent() {
  const navigate = useNavigate();
  const location = useLocation();
  const productBase = useProductAnalyticsBase();
  const { mutateAsync: createEvent } = useCreateEventMutation();
  const { mutateAsync: createEventTemplate } = useCreateEventTemplateMutation();
  const terreiroId = useCachedTerreiro()?.id ?? "";

  const submitEventByChat = useCallback(
    async ({
      data,
      memberFromActivityMap,
      insumoFromActivityMap,
      modelId,
    }: SubmitEventByChatInput) => {
      const builtPayload = buildEventFormChatPayload({
        data,
        terreiroId,
        memberFromActivityMap,
        insumoFromActivityMap,
      });

      if (!builtPayload) {
        throw new Error("Incomplete event data.");
      }

      const payload = modelId
        ? { ...builtPayload, isTemplate: false, sourceTemplateEventId: modelId }
        : builtPayload;
      const isTemplate = Boolean(payload.isTemplate);

      trackEventsEvent(
        "event_form_chat_submitted",
        { ...productBase, trigger: "mutation" },
        { is_template: isTemplate },
      );

      trackEventsEvent(
        modelId ? "event_model_create_submit_attempt" : "event_create_submit_attempt",
        { ...productBase, trigger: "mutation" },
        modelId
          ? { model_id: modelId, source: "form_chat" }
          : { is_template: isTemplate, source: "form_chat" },
      );

      try {
        if (isTemplate) {
          await createEventTemplate(payload as CreateEventPayload);
        } else {
          await createEvent(payload as CreateEventPayload);
        }

        trackEventsEvent(
          modelId ? "event_model_created" : "event_created",
          { ...productBase, trigger: "mutation" },
          modelId
            ? { model_id: modelId, source: "form_chat" }
            : { is_template: isTemplate, source: "form_chat" },
        );

        navigate(
          buildPathWithStatusTabSearch("/events", location.search, {
            rebalance: true,
          }),
        );
      } catch (error) {
        const apiError = extractAxiosApiError(error);
        if (
          apiError.message.toLowerCase().includes("inventory") ||
          apiError.message.toLowerCase().includes("insumo")
        ) {
          toast.error("Estoque insuficiente para os materiais selecionados.");
        }
        trackEventsError(
          "event_form_chat_submit_error",
          { ...productBase, trigger: "mutation" },
          error,
          { is_template: isTemplate },
        );
        throw error;
      }
    },
    [createEvent, createEventTemplate, location.search, navigate, productBase, terreiroId],
  );

  return { submitEventByChat };
}
